import gql from 'graphql-tag';
import client, { setWidgetSessionToken } from '../apollo-client';
import { getLocalStorageItem, initStorage, setLocalStorageItem } from '../common';
import { setLocale } from '../utils';
import widgetConnect from '../widgetConnect';
import { connection } from './connection';
import graphqTypes from './graphql';
import { IConnectResponse } from './types';
import asyncComponent from '../AsyncComponent';
import './sass/style.scss';
import './sass/output.css';
import { enabledServicesQuery } from '@/form/graphql';
import { EnabledServices } from '@/form/types';

const App = asyncComponent(() =>
  import( /* webpackChunkName: "MessengerApp" */'./containers/App')
)

widgetConnect({
  connectMutation: async (event: MessageEvent) => {
    const { setting, storage } = event.data;
    connection.setting = setting;
    const queryCustomerId = setting?.customerId
    const origin = setting?.origin;

    // CRITICAL: initStorage must run BEFORE any getLocalStorageItem read.
    // The in-memory `storage` object starts empty on every iframe load and
    // is only populated from the parent's localStorage by initStorage().
    // Reading before init would always return undefined → mutation gets sent
    // with `cachedCustomerId: undefined` → server creates a brand new
    // customer on every visit and the user loses their conversation history.
    initStorage(storage);

    // If the URL carries a customerId (deep-link / preview), it overrides
    // whatever was persisted from a previous session.
    if (queryCustomerId) setLocalStorageItem('customerId', queryCustomerId);

    const cachedCustomerId = queryCustomerId || getLocalStorageItem('customerId');

    client.query({
      query: gql(enabledServicesQuery),
      fetchPolicy: "network-only"
    }).then((res: any) => {
      console.log('enabledServices responfffse', res);
      if (res.data.enabledServices) {
        const { enabledServices } = res.data as EnabledServices;
        connection.enabledServices = enabledServices;
      }
    });

    let visitorId = cachedCustomerId;

    const generateVisitorId = async () => {
      const { getVisitorId } = await import('../widgetUtils');

      visitorId = await getVisitorId();
    }

    const browserLanguage = window.navigator?.language?.split('-')?.[0]
    const currentLanguage = connection.setting.language || browserLanguage
    connection.currentLanguage = currentLanguage

    if (!visitorId) await generateVisitorId()

    const isCallEnabled = connection.enabledServices.livekitcalls;

    return client.mutate({
      mutation: gql(graphqTypes.connect(isCallEnabled)),
      variables: {
        brandCode: setting.brand_id,
        email: setting.email,
        phone: setting.phone,
        code: setting.code,
        integrationId: setting.integrationId,
        cachedCustomerId,
        visitorId,
        origin: origin || document?.referrer || window.location?.host,
        isLead: Boolean(setting["IS_LEAD"]),
        // if client passed email automatically then consider this as user
        isUser: Boolean(setting.email),

        name: setting.name,
        data: setting.data,
        companyData: setting.companyData,
        currentLanguage
      }
    });
  },

  connectCallback: (data: { widgetsMessengerConnect: IConnectResponse }) => {
    const messengerData = data.widgetsMessengerConnect;
    if (!messengerData.messengerData) {
      throw new Error('Integration not found');
    }

    // Store the widget session token in memory. The HttpOnly cookie is the primary
    // transport; this feeds the in-memory header fallback in apollo-client. Never persisted.
    setWidgetSessionToken(messengerData.sessionToken);

    // save connection info
    connection.data = { ...messengerData, integrationId: messengerData?.integrationId || connection?.setting?.integrationId };

    // set language
    const browserLanguage = window.navigator?.language?.split('-')?.[0]
    const language = connection.setting.language || browserLanguage || messengerData.languageCode
    connection.currentLanguage = language
    setLocale(language);

    const customerId = connection?.setting?.customerId
    const labelId = connection?.setting?.labelId
    const userId = connection?.setting?.userId

    if (customerId) connection.data["customerId"] = customerId
    if (labelId) connection.data["labelId"] = labelId
    if (userId) connection.data["userId"] = userId

    // save customer id to identify visitor next time
    setLocalStorageItem('customerId', customerId || messengerData.customerId);

    // send connected message to ws server and server will save given
    // data to connection. So when connection closed, we will use
    // customerId to mark customer as not active

    // WebSocketLink will send this data to the server when subscribing or sending requests.
    // Server will save given
    // data to corresponding socket that handles this clients connection. 
    // So when connection is closed, we will use
    // customerId to mark customer as not active
    setLocalStorageItem('messengerDataJson', JSON.stringify(messengerData));
  },

  AppContainer: App
});
